'use client';

import { type FC, useMemo } from 'react';
import HoverPlayMedia from '@/app/components/shared/HoverPlayMedia';
import { MediaWithMaskBg } from '@/app/components/shared/MediaWithMaskBg';
import type { NewShowCaseList, ShowCaseList, UpdateInteractionStatusFn } from '@/app/types';
import { getAspectRatio, getCurrentItem } from '@/app/utils';
import { generateCommunityShareUrl } from '@/app/utils/client';
import { Link } from '@/i18n/routing';
import type { ViduV1UserMedal } from '../types';
import { HoverContent } from './HoverContent';

export type FeedCardAction = {
  key: string;
  title: string;
  onClick?: () => void;
};

export interface FeedCardProps {
  item: NewShowCaseList[number] | ShowCaseList[number];
  tag?: string;
  tooltipContent?: React.ReactNode;
  handleDetail: (id: string, traceId?: string) => void;
  handleVote?: UpdateInteractionStatusFn;
  handleUnVote?: UpdateInteractionStatusFn;
  avatar?: string;
  nickName?: string;
  medals: ViduV1UserMedal[];
  info?: string;
  title?: string;
  detail?: string;
  actions?: FeedCardAction[];
  onPlayStart?: () => void;
  eventName: string;
  eventProperties: Record<string, string>;
  borderRadius?: number;
  /** 强制 16/9 的比例 */
  forceVideoAspectRatio?: boolean;
  /** 限制宽高比在 16:9 和 9:16 之间，超出范围的会被裁剪为边界值 */
  limitAspectRatio?: boolean;
  hideEmptyVoteCount?: boolean;
}

export const FeedCard: FC<FeedCardProps> = (props) => {
  const {
    item,
    handleDetail,
    onPlayStart,
    eventName,
    forceVideoAspectRatio = false,
    limitAspectRatio = false,
    eventProperties,
  } = props;

  const currentItem = useMemo(() => {
    return getCurrentItem(item);
  }, [item]);

  const mediaAlt = useMemo(() => {
    if (!currentItem) return 'AI generated video';
    if ('title' in currentItem && currentItem.title) return currentItem.title;
    if ('brief' in currentItem && currentItem.brief) return currentItem.brief;
    return 'AI generated video';
  }, [currentItem]);

  // 渲染图片内容 一定是ViduV1MediaAsset结构
  const renderImageContent = () => (
    <div className="relative mb-3 w-full cursor-pointer">
      <div
        className="relative h-full w-full overflow-hidden rounded-4"
        style={{
          aspectRatio: getAspectRatio({
            size: currentItem?.creation?.size,
            forceVideoAspectRatio,
            limitAspectRatio,
          }),
        }}
      >
        {/* 列表使用小尺寸的图 */}
        <MediaWithMaskBg src={currentItem?.creation?.cover_uri || ''} useCover={limitAspectRatio} alt={mediaAlt} />
      </div>
    </div>
  );

  // 渲染视频内容
  const renderVideoContent = () => (
    <HoverPlayMedia
      id={currentItem?.id || ''}
      uri={currentItem?.creation?.uri}
      coverUri={currentItem?.creation?.cover_uri}
      alt={mediaAlt}
      className="mb-3 w-full"
      borderRadiusClassName="rounded-4"
      videoClassName={forceVideoAspectRatio ? 'object-contain' : limitAspectRatio ? 'object-cover' : ''}
      imageClassName={forceVideoAspectRatio ? 'object-contain' : limitAspectRatio ? 'object-cover' : ''}
      showMaskBg={forceVideoAspectRatio}
      aspectRatio={getAspectRatio({
        size: currentItem?.creation?.size,
        forceVideoAspectRatio,
        limitAspectRatio,
      })}
      onPlayStart={onPlayStart}
      eventName={eventName}
      eventProperties={eventProperties}
    />
  );

  return (
    <div className="group relative">
      <Link
        prefetch={false}
        key={currentItem?.id}
        href={generateCommunityShareUrl({ id: currentItem?.id || '', withOrigin: false })}
        onClick={() => handleDetail(currentItem?.id || '', item.traceId || '')}
      >
        {item?.type === 'image_asset' ? renderImageContent() : renderVideoContent()}
      </Link>
      {/* HoverContent 放在 Link 外面，避免事件冒泡问题 */}
      <HoverContent currentItem={currentItem} {...props} />
    </div>
  );
};
